import { useId, useState } from "react";
import { Lock, Plus, MoveRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Button, Card, CardDescription, CardHeader, CardTitle, Text, Textarea } from "@athena/ui";
import { CoordinatorNoteItem } from "./CoordinatorNoteItem";
import { NOTE_MAX_LENGTH, NOTES_PREVIEW_COUNT, type CoordinatorNote } from "../helpers/client";
import { useOpenedFrom } from "../helpers/openedFrom";

interface CoordinatorNotesCardProps {
    firstName: string;
    /** Every note, newest first. The card shows the newest few; the full view reads the same list. */
    notes: CoordinatorNote[];
    /** The first load, before any notes have arrived. */
    loading: boolean;
    loadFailed: boolean;
    /** Where "Read all" goes: the full record's notes tab. */
    readAllTo: string;
    /** Each resolves true once the change is saved; false when it was not, and the page has said why. */
    onAdd: (text: string) => Promise<boolean>;
    onEdit: (noteId: string, text: string) => Promise<boolean>;
    onDelete: (noteId: string) => Promise<boolean>;
}

/**
 * Private coordinator notes, shared by every coordinator of the client org and never shown to the
 * alum. Saved through `addClientNote`; the list is the page's `ClientNotes` query.
 */
export function CoordinatorNotesCard({
    firstName,
    notes,
    loading,
    loadFailed,
    readAllTo,
    onAdd,
    onEdit,
    onDelete,
}: CoordinatorNotesCardProps) {
    const { t } = useTranslation();
    // Passed on to the full record, so its way back to the profile keeps the profile's back link.
    const opened = useOpenedFrom();
    const noteId = useId();
    const [
        adding,
        setAdding,
    ] = useState<boolean>(false);
    const [
        draft,
        setDraft,
    ] = useState<string>("");

    const [
        saving,
        setSaving,
    ] = useState<boolean>(false);

    /** The draft is only cleared once the note is saved — a failed save must not lose what was written. */
    const save = async () => {
        setSaving(true);
        const saved = await onAdd(draft.trim());
        setSaving(false);
        if (!saved) return;
        setDraft("");
        setAdding(false);
    };

    return (
        <Card width="full">
            <CardHeader className="flex-row items-start gap-3 space-y-0">
                <span className="bg-muted text-muted-foreground flex h-8 w-8 shrink-0 items-center justify-center rounded-full">
                    <Lock className="h-4 w-4" aria-hidden />
                </span>
                <div className="min-w-0 flex-1 space-y-1">
                    <CardTitle>{t("admin.alumni.notes.title")}</CardTitle>
                    <CardDescription>{t("admin.alumni.notes.subtitle")}</CardDescription>
                </div>
                <div className="">
                    <Button
                        variant="outline"
                        size="sm"
                        className="w-auto shrink-0"
                        leftIcon={adding ? undefined : <Plus className="h-4 w-4" />}
                        onClick={() => setAdding((open) => !open)}
                    >
                        {adding ? t("common.actions.cancel") : t("admin.alumni.notes.add")}
                    </Button>
                    <div className="mr-2 flex justify-end py-2">
                        <Text size="xs" className="font-mono">
                            {Math.min(notes.length, NOTES_PREVIEW_COUNT)} of {notes.length}
                        </Text>
                    </div>
                </div>
            </CardHeader>

            {adding ? (
                <div className="flex flex-col gap-3 px-5 pb-5">
                    <label htmlFor={noteId} className="sr-only">
                        {t("admin.alumni.notes.label")}
                    </label>
                    <Textarea
                        id={noteId}
                        autoFocus
                        value={draft}
                        onChange={(event) => setDraft(event.target.value)}
                        maxLength={NOTE_MAX_LENGTH}
                        placeholder={t("admin.alumni.notes.placeholder", { name: firstName })}
                    />
                    <Button
                        size="sm"
                        className="self-end"
                        disabled={!draft.trim() || saving}
                        onClick={() => void save()}
                    >
                        {saving ? t("admin.alumni.notes.saving") : t("admin.alumni.notes.save")}
                    </Button>
                </div>
            ) : null}

            <div className="border-border border-t px-5 py-2">
                {loading ? (
                    <Text size="sm" muted className="py-4 text-center">
                        {t("admin.alumni.notes.loading")}
                    </Text>
                ) : loadFailed ? (
                    <Text size="sm" muted className="py-4 text-center">
                        {t("admin.alumni.notes.loadFailed")}
                    </Text>
                ) : notes.length === 0 ? (
                    <Text size="sm" muted className="py-4 text-center">
                        {t("admin.alumni.notes.empty", { name: firstName })}
                    </Text>
                ) : (
                    <ul className="divide-border divide-y">
                        {notes.slice(0, NOTES_PREVIEW_COUNT).map((note) => (
                            <CoordinatorNoteItem
                                key={note.id}
                                note={note}
                                onEdit={(text) => onEdit(note.id, text)}
                                onDelete={() => onDelete(note.id)}
                            />
                        ))}
                    </ul>
                )}
            </div>
            {/*
             * Outside the list (a list holds only its items) and outside the padded wrapper above,
             * whose bottom padding would otherwise sit under this row and push its text high.
             */}
            {!loading && !loadFailed && notes.length > 0 ? (
                <div className="border-border flex items-center justify-center border-t py-4">
                    <Link
                        to={readAllTo}
                        state={opened}
                        className="text-text-muted hover:text-text-heading flex items-center gap-2 text-sm"
                    >
                        {t("admin.alumni.notes.readAll", { count: notes.length })}
                        <MoveRight className="h-4 w-4" aria-hidden />
                    </Link>
                </div>
            ) : null}
        </Card>
    );
}
